import { Box } from '@material-ui/core'
import { maxMessageLength } from '@shared/core/events'
import { LANGUAGE_DISPLAY_NAMES, type SupportedLanguage } from '@shared/core/localized-defaults'
import { templateUsesVideoLink } from '@shared/core/sms/video-link-token'
import classNames from 'classnames'
import { forwardRef, memo } from 'react'
import { FormattedText, Typography } from 'src/modules/core/components'
import { ConsumedReminderSegments, CreateReminderHelper } from 'src/modules/reminder/components'
import uniqid from 'uniqid'
import type { MessageInputProps } from './message-input.interface'
import classes from './message-input.module.scss'

function buildPreviewCaption(confirmationsEnabled: boolean, messageLanguage: SupportedLanguage | undefined): string {
  if (!confirmationsEnabled) return 'SMS message preview'
  const caption = 'Preview. The confirmation link is added automatically'
  return messageLanguage && messageLanguage !== 'en'
    ? `${caption} in ${LANGUAGE_DISPLAY_NAMES[messageLanguage].english}`
    : caption
}

export const MessageInput = memo(
  forwardRef<HTMLInputElement & HTMLTextAreaElement, MessageInputProps>(
    (
      {
        id = uniqid(),
        error,
        label,
        className,
        wrapperClassName,
        labelClassName,
        showDynamicDataHelper,
        messageLanguage,
        confirmationsEnabled,
        messagePreview,
        messageLength,
        segmentsCount,
        messageTemplate,
        variant = 'primary',
        ...props
      },
      ref,
    ) => {
      const showVideoLinkHint = templateUsesVideoLink(messageTemplate)

      return (
        <div className={classNames(classes.root, wrapperClassName)}>
          {label && (
            <label htmlFor={id}>
              <div
                className={classNames(classes.labelWrapper, {
                  [classes.single]: !showDynamicDataHelper,
                })}
              >
                <Typography
                  className={classNames(classes.label, labelClassName, {
                    [classes.labelError]: !!error,
                    [classes.labelSecondary]: variant === 'secondary',
                  })}
                  variant="body"
                >
                  {label}
                </Typography>
                {showDynamicDataHelper && <CreateReminderHelper messageLanguage={messageLanguage} />}
              </div>
            </label>
          )}
          <Box
            display="flex"
            gridGap={10}
            className={classNames(classes.wrapper, {
              [classes.wrapperDisabled]: props.disabled,
            })}
          >
            <textarea
              className={classNames(classes.input, classes.textarea, className, {
                [classes.inputSecondary]: variant === 'secondary',
                [classes.inputError]: !!error,
              })}
              ref={ref}
              id={id}
              {...props}
            />
            <div className={classes.messagePreviewWrapper}>
              <Typography
                variant="span"
                className={classes.messagePreview}
                dangerouslySetInnerHTML={{ __html: messagePreview || 'Type a message to see the preview here' }}
              ></Typography>
              <div className={classes.confirmationLinkAlert}>
                {buildPreviewCaption(!!confirmationsEnabled, messageLanguage)}
              </div>
            </div>
          </Box>
          {error && (
            <Typography className={classes.error} variant="body">
              {error}
            </Typography>
          )}
          {showVideoLinkHint && (
            <Typography className={classes.hint} variant="body">
              <FormattedText id="configuration.step1.videoLinkHint" />
            </Typography>
          )}
          <div className={classNames(classes.countersWrapper)}>
            {/* Consumed reminder segments */}
            <ConsumedReminderSegments segmentsCount={segmentsCount} />
            {/* Current and max length of the message */}
            <Typography
              className={classNames(classes.sublabel, {
                [classes.error]: messageLength > maxMessageLength,
                [classes.counterError]: messageLength > maxMessageLength,
              })}
              variant="span"
            >
              <FormattedText
                id="configuration.step1.sublabel"
                values={{
                  current: messageLength ?? 0,
                  max: maxMessageLength,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Typography>
          </div>
        </div>
      )
    },
  ),
)

MessageInput.displayName = 'MessageInput'
